import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type { DealerSegmentRow } from 'app/pages/DealerSegmentSettingsComponent/components/DealerSegmentModal';
import {
  parseCSVToSegments,
  CSV_TEMPLATE_HEADERS,
} from 'app/pages/DealerSegmentSettingsComponent/utils/dealerSegmentCsvUtils';

const EMPTY_ROW: DealerSegmentRow = {
  field_name: '',
  field_value: '',
  qualifier: 'Starts with',
  primary_segment: '',
  secondary_segment: '',
  tertiary_segment: '',
  priority: 1,
};

interface DealerSegmentCsvUploadModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (rows: DealerSegmentRow[]) => void;
  saving?: boolean;
}

export const DealerSegmentCsvUploadModal = ({
  open,
  onClose,
  onApply,
  saving,
}: DealerSegmentCsvUploadModalProps) => {
  const [parsedRows, setParsedRows] = React.useState<DealerSegmentRow[]>([]);
  const [parseErrors, setParseErrors] = React.useState<string[]>([]);
  const [fileName, setFileName] = React.useState<string>('');
  const [processingFile, setProcessingFile] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const resetState = React.useCallback(() => {
    setParsedRows([]);
    setParseErrors([]);
    setFileName('');
    setProcessingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = React.useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleFileChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setFileName(file.name);
      setProcessingFile(true);
      const reader = new FileReader();
      reader.onload = () => {
        const text = String(reader.result ?? '');
        const { rows, errors } = parseCSVToSegments(text);
        setParsedRows(rows);
        setParseErrors(errors);
        setProcessingFile(false);
      };
      reader.onerror = () => {
        setProcessingFile(false);
      };
      reader.readAsText(file, 'UTF-8');
    },
    [],
  );

  const handleApply = React.useCallback(() => {
    if (parsedRows.length === 0) return;
    onApply(parsedRows);
    handleClose();
  }, [parsedRows, onApply, handleClose]);

  const handleDownloadTemplate = React.useCallback(() => {
    const blob = new Blob([CSV_TEMPLATE_HEADERS + '\n'], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dealer-segment-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const updateRow = React.useCallback(
    (index: number, field: keyof DealerSegmentRow, value: string | number) => {
      setParsedRows(prev =>
        prev.map((r, i) =>
          i === index
            ? { ...r, [field]: field === 'priority' ? Number(value) || 1 : value }
            : r,
        ),
      );
    },
    [],
  );

  const addRow = React.useCallback(() => {
    setParsedRows(prev => [...prev, { ...EMPTY_ROW }]);
  }, []);

  const deleteRow = React.useCallback((index: number) => {
    setParsedRows(prev => prev.filter((_, i) => i !== index));
  }, []);

  React.useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  const canApply = parsedRows.length > 0 && parseErrors.length === 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Bulk upload (CSV upload)</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            aria-hidden
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={
                processingFile || saving ? (
                  <CircularProgress
                    size={24}
                    thickness={4}
                    color="primary"
                    sx={{ flexShrink: 0 }}
                  />
                ) : (
                  <UploadFileIcon />
                )
              }
              onClick={() => fileInputRef.current?.click()}
              disabled={saving || processingFile}
              sx={
                processingFile || saving
                  ? { '&.Mui-disabled': { opacity: 1 } }
                  : undefined
              }
            >
              Choose CSV
            </Button>
            <Button size="small" onClick={handleDownloadTemplate}>
              Download template
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addRow}
              disabled={saving}
            >
              Add row
            </Button>
            {fileName && (
              <Typography variant="body2" color="text.secondary">
                {fileName}
                {parsedRows.length > 0 && ` · ${parsedRows.length} row(s)`}
              </Typography>
            )}
          </Box>
          {parseErrors.length > 0 && (
            <Typography color="error" variant="body2">
              {parseErrors.join(' ')}
            </Typography>
          )}
          {parsedRows.length > 0 && (
            <Box sx={{ overflow: 'auto', maxHeight: '60vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }} />
                    <TableCell>Field Name</TableCell>
                    <TableCell>Field Value</TableCell>
                    <TableCell>Qualifier</TableCell>
                    <TableCell>Primary</TableCell>
                    <TableCell>Secondary</TableCell>
                    <TableCell>Tertiary</TableCell>
                    <TableCell sx={{ width: 80 }}>Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedRows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell padding="none">
                        <IconButton
                          size="small"
                          onClick={() => deleteRow(i)}
                          disabled={saving}
                          aria-label="Delete row"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell padding="normal">
                        <TextField
                          size="small"
                          fullWidth
                          value={row.field_name}
                          onChange={e => updateRow(i, 'field_name', e.target.value)}
                          placeholder="e.g. voi_model"
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell padding="normal">
                        <TextField
                          size="small"
                          fullWidth
                          value={row.field_value}
                          onChange={e => updateRow(i, 'field_value', e.target.value)}
                          placeholder="e.g. CR-V"
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell padding="normal">
                        <TextField
                          size="small"
                          fullWidth
                          value={row.qualifier}
                          onChange={e => updateRow(i, 'qualifier', e.target.value)}
                          placeholder="Starts with"
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell padding="normal">
                        <TextField
                          size="small"
                          fullWidth
                          value={row.primary_segment}
                          onChange={e =>
                            updateRow(i, 'primary_segment', e.target.value)
                          }
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell padding="normal">
                        <TextField
                          size="small"
                          fullWidth
                          value={row.secondary_segment}
                          onChange={e =>
                            updateRow(i, 'secondary_segment', e.target.value)
                          }
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell padding="normal">
                        <TextField
                          size="small"
                          fullWidth
                          value={row.tertiary_segment}
                          onChange={e =>
                            updateRow(i, 'tertiary_segment', e.target.value)
                          }
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell padding="normal">
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 1, max: 99 }}
                          value={row.priority}
                          onChange={e =>
                            updateRow(i, 'priority', e.target.value)
                          }
                          variant="standard"
                          sx={{ width: 64 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={saving || !canApply}
        >
          {saving ? 'Applying…' : 'Apply bulk edit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
